import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { RoofTopI } from 'declarations/interfaces/roofTop';

type UseDealerSelectT = {
  roofTops: RoofTopI[];
  allChecked: boolean;
  searchText: string;
  isOpen: boolean;
  displayedRoofTops: RoofTopI[];
  tempSelectedRooftopIds: string[];
  containsText: (text: string, searchText: string) => boolean;
  isAllChecked: (roofTops: RoofTopI[]) => boolean;
  setIsOpen: (isOpen: boolean) => void;
  setAllChecked: (checked: boolean) => void;
  setSearchText: (text: string) => void;
  setDisplayedRoofTops: (roofTops: RoofTopI[]) => void;
  setTempSelectedRooftopIds: (ids: string[]) => void;
  handleAllClick: () => void;
  handleCheckboxClick: (roofTop: RoofTopI) => void;
  handleOnlyClick: (roofTop: string) => void;
  handleResetClick: () => void;
  handleApplyClick: () => void;
  clearSearchText: () => void;
};

const useDealerSelect = (): UseDealerSelectT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const {
    roofTopsList,
    setDealerSelectedRoofTopCount,
    setDealerSelectedRooftopIds,
    initialSelectedRooftopIds,
    setInitialSelectedRooftopIds,
  } = useTickets();
  const [roofTops, setRoofTops] = useState<RoofTopI[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState('');
  const [displayedRoofTops, setDisplayedRoofTops] = useState<RoofTopI[]>([]);
  const [tempSelectedRooftopIds, setTempSelectedRooftopIds] = useState<string[]>([]);
  const [allChecked, setAllChecked] = useState<boolean>(true);

  /**
   * Initializations
   */
  const containsText = (text: string, searchText: string) =>
    searchText === '' || text.toLowerCase().includes(searchText.toLowerCase());

  const isAllChecked = useCallback(
    (roofTops: RoofTopI[]) =>
      roofTops.every(roofTop => tempSelectedRooftopIds.includes(roofTop.id)),
    [tempSelectedRooftopIds]
  );

  const clearSearchText = () => {
    setSearchText('');
  };

  /**
   * Effects
   */

  useEffect(() => {
    // Initialize rooftops from the list, possibly filtering with a stored state if it exists
    const storedIds = localStorage.getItem('selectedRooftops');
    const storedRooftopIds = storedIds ? storedIds.split(',') : null;

    if (roofTopsList) {
      const defaultSelectedIds: string[] = storedRooftopIds || [];
      setRoofTops(roofTopsList);
      setTempSelectedRooftopIds(defaultSelectedIds);
      setInitialSelectedRooftopIds(defaultSelectedIds);
      setDealerSelectedRooftopIds(
        defaultSelectedIds.length === roofTopsList.length ? null : defaultSelectedIds.join(',')
      );
      setDealerSelectedRoofTopCount(defaultSelectedIds.length);
    }
  }, [
    roofTopsList,
    setDealerSelectedRoofTopCount,
    setDealerSelectedRooftopIds,
    setInitialSelectedRooftopIds,
  ]);

  useEffect(() => {
    const filteredDealerships = roofTops.filter(roofTop => containsText(roofTop.name, searchText));
    setDisplayedRoofTops(filteredDealerships);
    setAllChecked(isAllChecked(filteredDealerships));
  }, [roofTops, searchText, isAllChecked]);

  /**
   * Handlers
   */
  const handleAllClick = () => {
    const newSelectedIds = allChecked ? [] : roofTops.map(roofTop => roofTop.id);
    setTempSelectedRooftopIds(newSelectedIds);
    setAllChecked(!allChecked);
    setDealerSelectedRoofTopCount(newSelectedIds.length);
  };

  const handleCheckboxClick = (roofTop: RoofTopI) => {
    const updatedSelectedIds = tempSelectedRooftopIds.includes(roofTop.id)
      ? tempSelectedRooftopIds.filter(id => id !== roofTop.id)
      : [...tempSelectedRooftopIds, roofTop.id];
    setTempSelectedRooftopIds(updatedSelectedIds);
    setAllChecked(isAllChecked(displayedRoofTops));
    setDealerSelectedRoofTopCount(updatedSelectedIds.length);
  };

  const handleOnlyClick = (roofTopId: string) => {
    setTempSelectedRooftopIds([roofTopId]);
    setAllChecked(false);
    setDealerSelectedRoofTopCount(1);
  };

  const handleResetClick = () => {
    setTempSelectedRooftopIds(initialSelectedRooftopIds);
    setAllChecked(initialSelectedRooftopIds.length === roofTops.length);
    setDealerSelectedRoofTopCount(initialSelectedRooftopIds.length);
  };

  const handleApplyClick = () => {
    setDealerSelectedRooftopIds(
      tempSelectedRooftopIds.length === roofTops.length ? null : tempSelectedRooftopIds.join(',')
    );
    setInitialSelectedRooftopIds(tempSelectedRooftopIds);
    setDealerSelectedRoofTopCount(tempSelectedRooftopIds.length);
    setIsOpen(false);
    localStorage.setItem('selectedRooftops', tempSelectedRooftopIds.join(','));
    queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
  };

  return {
    roofTops,
    allChecked: isAllChecked(displayedRoofTops),
    searchText,
    isOpen,
    displayedRoofTops,
    tempSelectedRooftopIds,
    containsText,
    isAllChecked,
    setAllChecked,
    setIsOpen,
    setSearchText,
    setDisplayedRoofTops,
    setTempSelectedRooftopIds,
    handleAllClick,
    handleCheckboxClick,
    handleOnlyClick,
    handleResetClick,
    handleApplyClick,
    clearSearchText,
  };
};

export default useDealerSelect;
